/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

import {
  AbsoluteFill,
  ApolloError,
  Button,
  Divider,
  Heading,
  HStack,
  LoadingIndicator,
  ModalForm,
  ScrollPane,
  SimpleTabView,
  Spacer,
  Tab,
  Text,
  useShowMessage,
  VStack,
} from '../../core-ui';
import { loginAsAccount } from '../../support/loginAsAccount';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';
import { useParams, useNavigation, BackLink } from '../Navigation';
import { confirm } from '../App/AppDialog';
import { validateUser } from '../../support/safeValidators';
import { useAuthState } from '../AuthState';
import { toEnumObject } from '../../support/toEnumObject';
import { UserPermissionsInput } from '../../types/__generated__/globalTypes';

import AccountEditForm from './AccountEditForm';
import UserListView from './UserListView';
import { UserEditForm } from './UserEditForm';
import { CREATE_USER, GET_ACCOUNT, GET_ACCOUNT_USERS } from './graphql';
import { GetAccount, GetAccountVariables } from './__generated__/GetAccount';
import {
  UpdateAccount,
  UpdateAccountVariables,
} from './__generated__/UpdateAccount';
import {
  DeleteAccount,
  DeleteAccountVariables,
} from './__generated__/DeleteAccount';
import { CreateUser, CreateUserVariables } from './__generated__/CreateUser';

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: String!, $updates: UpdateAccountInput!) {
    updateAccount(id: $id, updates: $updates) {
      id
    }
  }
`;

const DELETE_ACCOUNT = gql`
  mutation DeleteAccount($id: String!) {
    deleteAccount(id: $id) {
      result
    }
  }
`;

export default function AccountDetailsPage() {
  const { navigate, backTo } = useNavigation();
  const params = useParams();
  const accountId = params.id ?? '';

  const auth = useAuthState();
  const myAccountId = auth.account.id;
  const showMessage = useShowMessage();
  const showApolloError = useShowApolloError();

  const { error, data } = useQuery<GetAccount, GetAccountVariables>(
    GET_ACCOUNT,
    {
      variables: { accountId },
      fetchPolicy: 'cache-and-network',
    },
  );

  const [tabIndex, setTabIndex] = useState(0);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const [createUser, { loading: isCreatingUser }] = useMutation<
    CreateUser,
    CreateUserVariables
  >(CREATE_USER, {
    onError: showApolloError,
    onCompleted: () => {
      showMessage('success', t('User created successfully'));
      setIsAddUserModalOpen(false);
    },
    refetchQueries: [GET_ACCOUNT_USERS],
  });

  const [updateAccount, { loading: isSaving }] = useMutation<
    UpdateAccount,
    UpdateAccountVariables
  >(UPDATE_ACCOUNT, {
    onError: showApolloError,
    onCompleted: () => {
      showMessage('success', t('Account saved successfully'));
    },
    refetchQueries: [GET_ACCOUNT],
  });

  const [deleteAccount] = useMutation<DeleteAccount, DeleteAccountVariables>(
    DELETE_ACCOUNT,
    {
      onError: showApolloError,
      onCompleted: () => {
        showMessage('success', t('Account deleted successfully'));
        backTo('/admin/accounts');
      },
    },
  );

  if (error) {
    return <ApolloError error={error} />;
  }
  if (!data) {
    return <LoadingIndicator />;
  }
  if (!data.account) {
    // TODO: Make a Not Found component
    return <Text>{t('Not Found')}</Text>;
  }
  const account = data.account;

  return (
    <VStack alignItems="stretch" flex={1} px={10} pt={2}>
      <BackLink to={['/admin/accounts']} alignSelf="start" />
      <HStack>
        <Heading as="h2" size="lg">
          {t('Edit Account')}
        </Heading>
        <Spacer />
        {tabIndex === 0 ? (
          <>
            <Button
              onClick={() => {
                loginAsAccount(account.id, {
                  onSuccess: () => navigate('/vendors'),
                });
              }}
            >
              {t('Login As')}
            </Button>
            <Button
              isDisabled={myAccountId === account.id}
              onClick={async () => {
                const proceed = await confirm(
                  t('Delete Account?'),
                  t(
                    'Are you sure you want to permanently delete this account?',
                  ),
                );
                if (!proceed) {
                  return;
                }
                deleteAccount({ variables: { id: account.id } });
              }}
            >
              {t('Delete Account')}
            </Button>
            <Button
              type="submit"
              form="account-edit"
              colorScheme="blue"
              isLoading={isSaving}
            >
              {t('Save')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setIsAddUserModalOpen(true)}>
              {t('New User')}
            </Button>
          </>
        )}
      </HStack>
      <Divider borderColor="gray.200" />

      <SimpleTabView
        onChange={(index) => {
          setTabIndex(index);
        }}
      >
        <Tab title={t('Details')} p={0}>
          <AbsoluteFill>
            <ScrollPane contentContainerProps={{ py: 4, px: 2 }}>
              <AccountEditForm
                formId="account-edit"
                account={account}
                onSave={async (account) => {
                  const { id, ...updates } = account;
                  await updateAccount({ variables: { id, updates } });
                }}
              />
            </ScrollPane>
          </AbsoluteFill>
        </Tab>
        <Tab
          title={t('Users ({count})', { count: account.users.length })}
          p={0}
        >
          <AbsoluteFill>
            <ScrollPane contentContainerProps={{ py: 4, px: 2 }}>
              <UserListView account={account} />
            </ScrollPane>
          </AbsoluteFill>
        </Tab>
      </SimpleTabView>
      <ModalForm
        title={t('New User')}
        isOpen={isAddUserModalOpen}
        submitButtonText={t('Save')}
        isSubmitting={isCreatingUser}
        onClose={() => setIsAddUserModalOpen(false)}
      >
        <UserEditForm
          user={null}
          onSave={(user) => {
            const { isValid, reason } = validateUser(user);
            if (!isValid) {
              showMessage('error', reason);
              return;
            }
            createUser({
              variables: {
                accountId: account.id,
                user: {
                  name: user.name,
                  email: user.email,
                  username: user.username,
                  password: user.password ?? '',
                  permissions: toEnumObject<UserPermissionsInput>(
                    user.permissions,
                  ),
                  suspended: user.suspended,
                },
              },
            });
          }}
        />
      </ModalForm>
    </VStack>
  );
}
